import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
import { useAppSelector } from '@store/hooks';
import fanCodeIcon from '@assets/fancode-icon.svg';
import flipkartIcon from '@assets/flipkart-icon.svg';
import atkinsIcon from '@assets/atkins-icon.svg';
import fleekIcon from '@assets/fleek-yellow-logo.svg';
import rokuIcon from '@assets/roku-logo.png';
const ExperienceContainer = styled.div `
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
`;
const SectionGraphics = styled.div `
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: visible;

  svg {
    position: absolute !important;
    z-index: 1 !important;
    opacity: 0.25 !important;
    pointer-events: none;
    display: block !important;

    &.section-bar-graph {
      left: auto !important;
      top: 100px !important;
      right: -80px !important;
      width: 80px !important;
      height: 40px !important;
    }

    &.section-pie {
      left: 0 !important;
      bottom: 0 !important;
      width: 32px !important;
      height: 32px !important;
    }
  }

  @media (max-width: 900px) {
    svg {
      width: 60px !important;
      height: 30px !important;
    }
  }
`;
const ExperienceTitle = styled.h2 `
  font-size: 1.7rem;
  font-weight: 700;
  color: #22334a;
  margin-bottom: 36px;
  position: relative;
  z-index: 2;
`;
const TimelineModern = styled.div `
  margin: 60px 0 48px 0;
  position: relative;
`;
const TimelineLine = styled.div `
  position: absolute;
  left: 5px;
  top: -3%;
  bottom: 0;
  width: 4px;
  background: #ffce3c;
  border-radius: 2px;
  height: 103%;
`;
const TimelineLineArrow = styled.svg `
  position: absolute;
  left: -26px;
  top: -26px;
  width: 56px;
  height: 56px;
`;
const TimelineItem = styled.div `
  display: flex;
  align-items: flex-start;
  margin-bottom: 48px;
  position: relative;
  min-height: 80px;
`;
const TimelineDot = styled.div `
  position: absolute;
  z-index: 2;
  width: 24px;
  height: 24px;
  background: ${props => props.$isCurrent ? '#0a66c2' : '#ffce3c'};
  border-radius: 50%;
  border: 4px solid #fff;
  box-shadow: ${props => props.$isCurrent
    ? '0 0 0 6px #eaf4ff, 0 2px 8px rgba(10, 102, 194, 0.13)'
    : '0 2px 8px rgba(255, 206, 60, 0.12)'};
  margin-left: -12px;
  margin-right: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 50%;
  transform: translate(7px, -16px);
`;
const TimelineConnector = styled.div `
  position: absolute;
  left: 12px;
  top: 48%;
  width: 32px;
  height: 4px;
  background: ${props => props.$isCurrent ? '#0a66c2' : '#ffce3c'};
  border-radius: 2px;
  transform: translateY(-50%);
  z-index: 1;
`;
const TimelineCard = styled.div `
  background: ${props => props.$isCurrent
    ? 'linear-gradient(90deg, #eaf4ff 60%, #fff 100%)'
    : '#fff'};
  border: 2px solid ${props => props.$isCurrent ? '#0a66c2' : '#ffce3c'};
  border-radius: 16px;
  box-shadow: ${props => props.$isCurrent
    ? '0 4px 24px 0 rgba(10, 102, 194, 0.13), 0 2px 8px rgba(0, 0, 0, 0.06)'
    : '0 2px 12px 0 rgba(255, 206, 60, 0.07)'};
  padding: 24px 28px 18px 28px;
  min-width: 320px;
  max-width: 420px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  margin-left: 43px;

  @media (max-width: 700px) {
    min-width: 0;
    max-width: 98vw;
    padding: 16px 10vw 14px 10vw;
  }
`;
const TimelineTitle = styled.div `
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.$isCurrent ? '#0a66c2' : '#22334a'};
`;
const TimelineDate = styled.div `
  font-size: 1.1rem;
  font-weight: 600;
  color: #22334a;
  opacity: 0.8;
`;
const TimelineDesc = styled.div `
  font-size: 0.9rem;
  line-height: 1.5em;
  color: #22334a;
  opacity: 0.85;
`;
const TimelineLogo = styled.img `
  position: absolute;
  top: 10px;
  right: 18px;
  margin: 0;
  vertical-align: middle;
  object-fit: contain;
  z-index: 2;
`;
const Experience = () => {
    const experiences = useAppSelector((state) => state.portfolio.experiences);
    // Map logo strings to actual imported assets
    const getLogoAsset = (logoName) => {
        switch (logoName) {
            case 'roku-logo.png':
                return rokuIcon;
            case 'fleek-yellow-logo.svg':
                return fleekIcon;
            case 'fancode-icon.svg':
                return fanCodeIcon;
            case 'flipkart-icon.svg':
                return flipkartIcon;
            case 'atkins-icon.svg':
                return atkinsIcon;
            default:
                return rokuIcon; // fallback to Roku since it's current
        }
    };
    return (_jsxs(ExperienceContainer, { children: [_jsxs(SectionGraphics, { children: [_jsx("svg", { className: "section-bar-graph", width: "80", height: "40", viewBox: "0 0 80 40", children: _jsxs("g", { opacity: "0.25", children: [_jsx("rect", { x: "5", y: "25", width: "8", height: "15", fill: "#ffce3c" }), _jsx("rect", { x: "18", y: "15", width: "8", height: "25", fill: "#ffce3c" }), _jsx("rect", { x: "31", y: "10", width: "8", height: "30", fill: "#ffce3c" }), _jsx("rect", { x: "44", y: "5", width: "8", height: "35", fill: "#ffce3c" }), _jsx("polyline", { points: "9,25 22,15 35,10 48,5", fill: "none", stroke: "#111", strokeWidth: "2" })] }) }), _jsx("svg", { className: "section-pie", width: "32", height: "32", viewBox: "0 0 32 32", children: _jsxs("g", { opacity: "0.25", children: [_jsx("circle", { r: "16", cx: "16", cy: "16", fill: "#ffce3c" }), _jsx("path", { d: "M16 16 L16 0 A16 16 0 0 1 32 16 Z", fill: "#111" })] }) })] }), _jsx(ExperienceTitle, { children: "Experience" }), _jsxs(TimelineModern, { children: [_jsx(TimelineLine, { children: _jsx(TimelineLineArrow, { width: "56", height: "56", viewBox: "0 0 24 24", fill: "#ffce3c", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M17.2863 15.7075C16.8963 16.0975 16.2663 16.0975 15.8763 15.7075L11.9963 11.8275L8.11626 15.7075C7.72626 16.0975 7.09626 16.0975 6.70626 15.7075C6.31626 15.3175 6.31626 14.6875 6.70626 14.2975L11.2963 9.7075C11.6863 9.3175 12.3163 9.3175 12.7063 9.7075L17.2963 14.2975C17.6763 14.6775 17.6763 15.3175 17.2863 15.7075Z", fill: "#ffce3c" }) }) }), experiences.map((exp) => (_jsxs(TimelineItem, { "$isCurrent": exp.isCurrent, children: [_jsx(TimelineDot, { "$isCurrent": exp.isCurrent }), _jsx(TimelineConnector, { "$isCurrent": exp.isCurrent }), _jsxs(TimelineCard, { "$isCurrent": exp.isCurrent, children: [_jsx(TimelineLogo, { src: getLogoAsset(exp.logo), alt: `${exp.title} Logo`, width: exp.logoSize.width, height: exp.logoSize.height }), _jsx(TimelineTitle, { "$isCurrent": exp.isCurrent, children: exp.title }), _jsx(TimelineDate, { children: exp.date }), _jsx(TimelineDesc, { children: exp.description })] })] }, exp.id)))] })] }));
};
export default Experience;
